import AuthBase from "../../AuthBase";
import {useContext, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router";
import Masonry from '@mui/lab/Masonry';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import serviceTemplate from "./ServiceTemplate";
import Loading from "../../../components/Loading";
import SubscriptionContext from "../../../context/SubscriptionContext";
import General from "./components/General";
import Visibility from "./components/Visibility";
import Image from "./components/Image";
import Category from "./components/Category";
import Invoice from "./components/Invoice";
import Payment from "./components/Payment";
import MaxDate from "./components/MaxDate";
import {useSnackbar} from "notistack";
import {mergeDataIfNotNull} from "../../../utils/misc";
import Questions from "./components/Questions";
import Colleagues from "./components/Colleagues";
import useServiceQueries from "../../../hooks/queries/useServiceQueries";
import useBusinessQueries from "../../../hooks/queries/useBusinessQueries";
import Recurring from "./components/Recurring";

const ServiceDetail = () => {
    const navigate = useNavigate()

    const {billingAvailable} = useContext(SubscriptionContext)
    const {enqueueSnackbar} = useSnackbar()

    const params = useParams()

    const generalRef = useRef(null)
    const visibilityRef = useRef(null)
    const imageRef = useRef(null)
    const categoryRef = useRef(null)
    const invoiceRef = useRef(null)
    const paymentRef = useRef(null)
    const maxDatesRef = useRef(null)

    const {useServiceQuery, updateServiceMutation} = useServiceQueries()
    const {businessGetBusinessQuery} = useBusinessQueries()

    const {data: service, isPending: serviceLoading} = useServiceQuery(params.serviceId)
    const {data: business, isPending: businessLoading} = businessGetBusinessQuery

    const [editedService, setEditedService] = useState(service || serviceTemplate)

    const hasUnsavedChanges = JSON.stringify(editedService) !== JSON.stringify(service);

    useEffect(() => {
        if (service) {
            setEditedService(service);
        }
    }, [service]);

    const savePageData = () => {
        const refData = [
            generalRef.current.getData(),
            visibilityRef.current.getData(),
            imageRef.current.getData(),
            categoryRef.current.getData(),
            invoiceRef.current.getData(),
            paymentRef.current.getData(),
            maxDatesRef.current.getData(),
        ]
        const invalidRefs = refData.filter(ref => ref === null)
        if (invalidRefs.length === 0) {
            const allData = mergeDataIfNotNull(refData);
            const isFile = allData?.image instanceof File;
            if (!isFile) delete allData['image']
            updateServiceMutation.mutate({id: service?.id, data: allData}, {
                onError: errors => {
                    errors.errors.forEach(err => {
                        if (err.field) {
                            // field-specific error logic
                            enqueueSnackbar(`${err.message} - ${err.field}`, {variant: 'error'});
                        } else {
                            // general notification
                            enqueueSnackbar(err.message, {variant: 'error'});
                        }
                    });
                },
            })
        } else {
            enqueueSnackbar('Kérlek javísd a hibákat!', {variant: 'error'})
        }
    }

    const copyBookingLink = () => {
        navigator.clipboard.writeText(`https://${business?.subdomain}.dinobooking.hu/book?c=${service.category}&s=${service.id}`)
            .then(() => enqueueSnackbar('Másolva', {variant: 'success'}))
    }

    const buttons = [
        {
        name: 'Változtatások mentése',
        props: {
            variant: 'contained',
            disabled: !hasUnsavedChanges,
            endIcon: <SaveRoundedIcon/>,
            onClick: savePageData
        }
    }, {
        name: 'Foglalási link másolása',
        props: {
            variant: 'outlined',
            endIcon: <ContentCopyRoundedIcon/>,
            onClick: copyBookingLink
        }
    }]

    const buttonsMobile = [
        {
        name: 'Mentés',
        props: {
            variant: 'contained',
            size: 'small',
            disabled: !hasUnsavedChanges,
            endIcon: <SaveRoundedIcon/>,
            onClick: savePageData
        }
    }, {
        name: 'link',
        props: {
            variant: 'outlined',
            size: 'small',
            endIcon: <ContentCopyRoundedIcon/>,
            onClick: copyBookingLink
        }
    }]

    if (serviceLoading || businessLoading) return <Loading isLoading />

    return <AuthBase label={service?.name} buttons={buttons} filterButtonsMobile={buttonsMobile}>
        <div className="main">
            <Button
                variant='text'
                startIcon={<ArrowBackIosNewRoundedIcon/>}
                onClick={() => navigate(`/categories/${service?.category}`)}>
                vissza
            </Button>

            <Masonry columns={{xs: 1, sm: 1, md: 2, xl: 2}} spacing={2} sx={{margin: 0}}>

                {/*General data*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Általános adatok</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Add meg a szolgáltatás nevét és leírását!
                    </Typography>
                    <General reference={generalRef} data={editedService} setData={setEditedService}/>
                </div>

                {/*Visibility*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Láthatóság</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Ha nem szeretnéd, hogy a szolgáltatás látható legyen
                        a kliensek számára akkor kikapcsolhatod. (Az elrejtés előtt leadott foglalások nem fognak
                        törlődni!)
                    </Typography>
                    <Visibility reference={visibilityRef} data={editedService} setData={setEditedService}/>
                </div>

                {/*Picture*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Szolgáltatás képe</Typography>
                    <Typography variant='body2' color='#536B74'>Tölts fel egy szolgáltatásra jellemző
                        képet!</Typography>
                    <Image reference={imageRef} data={editedService} setData={setEditedService}/>
                </div>

                {/*Category*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Szolgáltatás kategória</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Válaszd ki melyik kategóriába tartozik a szolgáltatás!
                    </Typography>
                    <Category reference={categoryRef} data={editedService} setData={setEditedService}/>
                </div>


                {/*Invoice*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Számlázási beállítások</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Válaszd ki hogyan szeretnél számlázni a vevőid felé!
                    </Typography>
                    <Invoice
                        reference={invoiceRef}
                        data={editedService} setData={setEditedService}
                        paymentAuthorized={business?.payment_authorized} billingAvailable={billingAvailable()}/>
                </div>

                {/*Billing methods*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Fizetési lehetőségek</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Állítsd be milyen fizetési lehtőségek lehetségesek a szolgáltatásnál!
                    </Typography>
                    <Payment
                        reference={paymentRef}
                        data={editedService} setData={setEditedService}
                        paymentAuthorized={business?.payment_authorized} billingAvailable={billingAvailable()}/>
                </div>

                {/*Max dates*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Foglalások elfogadása</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Állítsd be meddig szeretnél előre foglalásokat elfogadni a szolgáltatásnál!
                    </Typography>
                    <MaxDate
                        reference={maxDatesRef}
                        data={editedService} setData={setEditedService}
                    />
                </div>

                {/*Occurrence*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Szolgáltatás megjelenése</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Állítsd be mikor lehessen foglalni a szolgáltatást!
                    </Typography>
                    <Recurring
                        reference={maxDatesRef}
                        data={editedService} setData={setEditedService}
                    />
                </div>

                {/*Colleagues*/}
                <Colleagues/>

                {/*Questions*/}
                <Questions/>

            </Masonry>
        </div>


        {/*<Prompt*/}
        {/*    when={hasUnsavedChanges}*/}
        {/*    message="Elmentetlen változtatásaid vannak, biztosan elmész erről az oldalról?"*/}
        {/*/>*/}

    </AuthBase>

}

export default ServiceDetail